package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.SourceViewDto
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.text.FitResult
import dev.lingvo.shared.screenshots.text.FontStack

/** Undo writes only the old value against the saved revision; it never reinstates an approval. */
internal class CaptionReceipt(
    val previous: CaptionSnapshot, val saved: CaptionSnapshot, private val keys: CaptionKeysPort,
    private val restored: ((CaptionSnapshot) -> Unit)? = null,
) {
    private var used = false
    suspend fun undo(): CaptionSnapshot {
        check(!used) { "This caption undo has already been used" } // i18n-ignore: internal contract diagnostic
        used = true
        return try { keys.write(saved, previous.value).also { restored?.invoke(it) } } catch (failure: Exception) { used = false; throw failure }
    }
}

/** Caption edits bypass design history and debounce fitting through an owned cancellable timer. */
internal class CaptionBinding(
    initial: CaptionSnapshot, private val keys: CaptionKeysPort, private val fitter: CaptionFitPort,
    private val schedule: (Int, () -> Unit) -> Int, private val cancel: (Int) -> Unit,
    private val changed: (CaptionSnapshot) -> Unit,
) {
    var snapshot = initial
        private set
    private var timer: Int? = null
    private var disposed = false
    private var operation = 0
    fun acceptSaved(restored: CaptionSnapshot) {
        require(restored.project == snapshot.project && restored.keyId == snapshot.keyId && restored.locale == snapshot.locale)
        val draft = if (snapshot.draft != snapshot.value) snapshot.draft else restored.value
        operation++
        snapshot = restored.copy(draft = draft, layer = snapshot.layer, font = snapshot.font, saving = false,
            fit = fitter.fit(draft, restored.locale, snapshot.layer))
        if (!disposed) changed(snapshot)
    }
    fun updateLayer(layer: dev.lingvo.shared.screenshots.doc.TextLayer) {
        if (layer == snapshot.layer || disposed) return
        snapshot = snapshot.copy(layer = layer, fit = fitter.fit(snapshot.draft, snapshot.locale, layer))
        changed(snapshot)
    }
    fun input(value: String) {
        if (disposed || !snapshot.editable) return
        timer?.let(cancel)
        snapshot = snapshot.copy(draft = value, error = null)
        changed(snapshot)
        timer = schedule(50) { timer = null; if (!disposed) refit() }
    }
    fun cancelDraft() {
        timer?.let(cancel); timer = null
        snapshot = snapshot.copy(draft = snapshot.value, error = null,
            fit = fitter.fit(snapshot.value, snapshot.locale, snapshot.layer))
        if (!disposed) changed(snapshot)
    }
    private fun refit() {
        snapshot = snapshot.copy(fit = fitter.fit(snapshot.draft, snapshot.locale, snapshot.layer))
        changed(snapshot)
    }
    /** Web-font completion refits the current draft without replacing it or touching history. */
    fun refreshFit() { if (!disposed) refit() }
    fun refreshFont(font: FontStack) {
        if (disposed) return
        snapshot = snapshot.copy(font = font)
        refit()
    }
    suspend fun save(): CaptionReceipt? {
        if (disposed || !snapshot.editable || snapshot.draft == snapshot.value) return null
        timer?.let(cancel); timer = null
        refit()
        if (snapshot.fit is FitResult.Refused) return null
        val before = snapshot
        val token = ++operation
        snapshot = snapshot.copy(saving = true); changed(snapshot)
        return try {
            val saved = keys.write(before, before.draft).copy(saving = false)
            if (token == operation) {
                snapshot = saved
                if (!disposed) changed(snapshot)
            }
            CaptionReceipt(before, saved, keys, ::acceptSaved)
        } catch (failure: Exception) {
            if (token == operation) {
                snapshot = before.copy(saving = false, error = failure.message)
                if (!disposed) changed(snapshot)
            }
            throw failure
        }
    }
    fun dispose() { disposed = true; timer?.let(cancel); timer = null }
}
